import {
    CreateEmailTemplateDto,
    CreateNotificationUnsubscribeFeedbackDto,
    CreateReportDto,
    EmailDto,
    ICreateEmailTemplateDto,
    ICreateNotificationUnsubscribeFeedbackDto,
    ICreateReportDto,
    IEmailDto,
    IListFolderDto,
    IListQueryDto,
    IUpdateFolderPropertyDto,
    IUpdateReportDto,
    IUpdateReportSharedWithDto,
    IUserListDto,
    IUserPreferencesDto,
    ListFolderDto,
    ListQueryDto,
    ListSortDirection,
    ReportType,
    UpdateFolderPropertyDto,
    UpdateReportDto,
    UpdateReportSharedWithDto,
    UserApiClient,
    UserListDto,
    UserPreferencesDto
} from "generated/user-backend";
import { API, appInsightsService } from "app/utils/constants";
import { useMutation, useQuery } from "react-query";
import { AppQueryClient } from "./clients/query-client";

// The local environment has a trailing slash in the API_SERVER_URL, but test and prod do not.
const baseUrl : string = API.API_SERVER_URL.endsWith('/') ? API.API_SERVER_URL.slice(0,-1) : API.API_SERVER_URL;

//#region Queries
export const useGetUserDetails = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(
        ['useGetUserDetails'],
        async () => {
            return await userApi.preference_GetCurrentUserInfo()
        },
        { cacheTime: 0 }
    );
};

export const useGetUserPreferences = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(
        ['useGetUserPreferences'],
        async () => {
            return await userApi.preference_GetUserPreferences();
        },
        { cacheTime: 0 }
    );
};

export const useFetchUsersListByRole = (team?: number) => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(['useFetchUsersListByRole', team], async () => {
        return await userApi.user_SearchAdminUsers(null,null,team);
    });
};

//list of reports
export const useGetReports = (
    pageNumber: number,
    pageSize: number,
    searchFilter?: string,
    sortProperty: 'DisplayName' | 'Type' | 'ModifiedOn' = 'ModifiedOn',
    sortDirection: string = '1',
    filterBookmarkedReports?: boolean,
    reportType?: ReportType
) => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(
        ['useGetReports', pageNumber, pageSize, sortProperty, sortDirection, filterBookmarkedReports, reportType],
        async () => {
            return await userApi.report_GetReports(
                filterBookmarkedReports,
                null,
                sortProperty,
                sortDirection === "1"? ListSortDirection.Descending : ListSortDirection.Ascending,
                pageNumber,
                pageSize,
                searchFilter,
                reportType
            )
        },
        { cacheTime: 0, enabled: false }
    );
};

//get user has bookmarked reports
export const useGetUserHasBookmarkedReports = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(
        ['useGetUserHasBookmarkedReports'],
        async () => {
            return await userApi.report_HasBookmarkedReports();
        },
        { cacheTime: 0 }
    );
};

//get reports by id
export const useGetReportById = (reportId: string) => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(
        ['useGetReportById', reportId],
        async () => {
            return await userApi.report_GetReportById(reportId);
        },
        { enabled: reportId?.trim() ? true : false }
    );
};

interface PageInfo {
    pageNumber: number;
    pageSize: number;
}

export const useNotifications = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const requestTypeMutation = useMutation((data: PageInfo) => {
        return userApi.notification_GetUserNotifications(data.pageNumber, data.pageSize)
    }, {
        onSuccess: () => {
        }
    });
    return requestTypeMutation;
};

export const useNotificationsFull = (pageNumber: number, pageSize: number) => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(['notifications', 'pageNumber'], async () => {
        return await userApi.notification_GetUserNotifications(pageNumber, pageSize);
    });
};

export const useNotificationsCount = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(['useNotificationsCount'], async () => {
        return await userApi.notification_GetUnreadNotificationCount();
    });
};

//get all emails
export const useGetEmailsList = (
    pageNumber: number,
    pageSize: number,
    sortProperty: string = null,
    sortDirection: number = null
) => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(
        ['useGetEmailsList', pageNumber],
        async () => {
            return await userApi.email_GetEmails(pageNumber, pageSize)
        },
        { cacheTime: 0, enabled: false }
    );
};

//get email by id
export const useGetEmailById = (emailId: string) => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(
        ['useGetEmailById', emailId],
        async () => {
            if (emailId.trim() !== '') {
                return await userApi.email_GetEmailById(emailId);
            }
        },
        { enabled: false }
    );
};

//get all email templates
export const useGetEmailTemplatesList = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(
        ['useGetEmailTemplatesList'],
        async () => {
            return await userApi.emailTemplate_GetEmailTemplates()
        },
        { enabled: true, cacheTime: 0 }
    );
};

//get email by id
export const useGetEmailTemplateById = (emailId: string) => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(
        ['useGetEmailTemplateById', emailId],
        async () => {
            if (emailId.trim() !== '') {
                return await userApi.emailTemplate_GetEmailTemplateById(emailId);
            }
        },
        { enabled: false }
    );
};

// gets a list of queries saved by a user
export const useGetUserSavedLists = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(['useGetUserSavedLists'], async () => {
        return await userApi.list_GetUserLists();
    }, { enabled: true });
};

// gets a list of all ticket submitters
export const useGetSubmitters = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(['useGetSubmitters'], async () => {
        return await userApi.submitter_GetAllSubmitters();
    }, { enabled: true });
};

export const useGetUsersList = (
    searchValue: string,
    type: 'Admin' | null = null,
    team?: number,
    count?: number
) => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    return useQuery(['getUsersList', searchValue, team], async () => {
        if(searchValue.trim() === '' && team === undefined) {
            return [];
        } else if(type === "Admin") {
            return await userApi.user_SearchAdminUsers(searchValue,count,team);
        }
        if(searchValue.trim() === '') {
            return [];
        }
        return await userApi.user_SearchUsers(searchValue);
    });
};
//#endregion
//#region Mutations
export const useNotificationsRead = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation(() => userApi.notification_MarkAsRead(), {
        onSuccess: () => {
            appInsightsService.logEvent('useNotificationsRead was Succesful');
        }
    });
    return response;
};

export const useNewsSubscription = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const mutation = useMutation(() => userApi.preference_SubscribeNewsletter(), {
        onSuccess: () => {
            appInsightsService.logEvent('useNewsSubscription was Succesful');
        }
    });
    return mutation;
};

export const useKnowledgeArticleSubscription = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const mutation = useMutation(() => userApi.preference_SubscribeKnowledgeCenter(), {
        onSuccess: () => {
            appInsightsService.logEvent('useKnowledgeArticleSubsc-iption was Succesful');
        }
    });
    return mutation;
};

export const useNewsUnSubscription = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const mutation = useMutation(() => userApi.preference_UnsubscribeNewsletter(), {
        onSuccess: () => {
            appInsightsService.logEvent('useNewsUnSubscription was Succesful');
        }
    });
    return mutation;
};

export const useKnowledgeArticleUnSubscription = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const mutation = useMutation(() => userApi.preference_UnsubscribeKnowledgeCenter(), {
        onSuccess: () => {
            appInsightsService.logEvent('useKnowledgeArticleUnSubscription was Succesful');
        }
    });
    return mutation;
};

/**
 * Mutation used to store user preference
 * */
export const useStorePreference = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const mutation = useMutation((data: IUserPreferencesDto) => userApi.preference_UpdateUserPreferences(new UserPreferencesDto(data)), {
        onSuccess: () => {
            // Query Invalidations
            appInsightsService.logEvent('useStorePreference was Succesful');
        }
    });
    return mutation;
};

/**
 * Mutation used to delete report
 * */
export const useDeleteReport = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((reportId: string) => userApi.report_DeleteReport(reportId), {
        onSuccess: () => {
            // Query Invalidations
            appInsightsService.logEvent('useDeleteReport was Succesful');
        }
    });
    return response;
};

/**
 * Mutation used to share report
 * */
export const useShareReport = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((sharedWith: IUpdateReportSharedWithDto) => userApi.report_ShareReport(new UpdateReportSharedWithDto(sharedWith)), {
        onSuccess: () => {
            // Query Invalidations
            appInsightsService.logEvent('useShareReport was Succesful');
        }
    });
    return response;
};

/**
 * Mutation used to bookmark report
 * */
export const useBookmarkReport = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((reportId: string) => userApi.report_BookmarkReport(reportId), {
        onSuccess: () => {
            // Query Invalidations
            appInsightsService.logEvent('useBookmarkReport was Succesful');
        }
    });
    return response;
};

/**
 * Mutation used to update report
 * */
export const useUpdateReport = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((report: IUpdateReportDto) => {
        // Nswag is failing to include the id in the autogenerated constructor for UpdateReportDtos. However, it does include it in the init.
        const myUpdate = new UpdateReportDto(report);
        myUpdate.init(report);
        return userApi.report_UpdateReport(myUpdate);
    }, {
        onSuccess: () => {
            // Query Invalidations
            appInsightsService.logEvent('useUpdateReport was Succesful');
        }
    });
    return response;
};

/**
 * Mutation used to add report
 * */
export const useAddReport = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((report: ICreateReportDto) => userApi.report_CreateReport(new CreateReportDto(report)), {
        onSuccess: () => {
            // Query Invalidations
            appInsightsService.logEvent('useAddReport was Succesful');
        }
    });
    return response;
};

// Create email
export const useSendEmail = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const commentsTagsMutation = useMutation((params: IEmailDto) => userApi.email_CreateEmail(new EmailDto(params)), {
        onSuccess: () => {
            // Query Invalidations
            appInsightsService.logEvent('useSendEmail was Succesful');
        }
    });
    return commentsTagsMutation;
};

// Create email template
export const useCreateEmailTemplate = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const commentsTagsMutation = useMutation((params: ICreateEmailTemplateDto) => userApi.emailTemplate_CreateEmailTemplate(new CreateEmailTemplateDto(params)), {
        onSuccess: () => {
            // Query Invalidations
            appInsightsService.logEvent('useCreateEmailTemplate was Succesful');
        }
    });
    return commentsTagsMutation;
};

// Delete email template
export const useDeleteEmailTemplate = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((params: string) => userApi.emailTemplate_DeleteEmailTemplate(params), {
        onSuccess: () => {
            // Query Invalidations
            appInsightsService.logEvent('useDeleteEmailTemplate was Succesful');
        }
    });
    return response;
};

// UnsubscribeFeedback
export const useUnsubscribeFeedback = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((params: ICreateNotificationUnsubscribeFeedbackDto) => userApi.notification_CreateReport(new CreateNotificationUnsubscribeFeedbackDto(params)), {
        onSuccess: () => {
            // Query Invalidations
            appInsightsService.logEvent('useUnsubscribeFeedback was Succesful');
        }
    });
    return response;
};

// Checking from below here
/**
 * Mutation to save a new list builder list
 */
export const useSaveList = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((data: IUserListDto) => userApi.list_UpdateUserLists(new UserListDto(data)), {
        onSuccess: () => {
            AppQueryClient.invalidateQueries('useGetUserSavedLists');
        }
    });

    return response;
}

/**
 * Mutation to favorite a list builder list
 */
export const useFavoriteList = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((data: string) => userApi.list_StarUserQuery(data), {
        onSuccess: () => {
            //AppQueryClient.invalidateQueries('useGetUserSavedLists');
        }
    });

    return response;
}

/**
 * Mutation to update a list builder list
 */
export const useUpdateList = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((data: IUserListDto) => userApi.list_UpdateUserListProperties(new UserListDto(data)), {
        onSuccess: () => {

        }
    });

    return response;
}

/**
 * Mutation to update a list builder folder
 */
export const useUpdateFolder = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((data: IUpdateFolderPropertyDto) => userApi.list_UpdateFolderProperties(new UpdateFolderPropertyDto(data)), {
        onSuccess: () => {

        }
    });

    return response;
}

/**
 * Mutation to delete a list builder folder and its contents
 */
export const useDeleteFolder = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((data: string) => userApi.list_DeleteUserFolder(data), {
        onSuccess: () => {
            AppQueryClient.invalidateQueries('useGetUserSavedLists');
        }
    });

    return response;
}

/**
 * Mutation to delete a list builder list
 */
export const useDeleteList = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((data: string) => userApi.list_DeleteUserList(data), {
        onSuccess: () => {
            AppQueryClient.invalidateQueries('useGetUserSavedLists');
        }
    })

    return response;
}

/**
 * Mutation to update the query parameters of a list builder list
 */
export const useUpdateQuery = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((data: IListQueryDto) => userApi.list_UpdateListQuery(new ListQueryDto(data)), {
        onSuccess: () => {

        }
    });

    return response;
}

/**
 * Mutation to save the reordering of lists within a folder
 */
export const useReorderLists = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((data: IListFolderDto) => userApi.list_ReorderLists(new ListFolderDto(data)));

    return response;
}

/**
 * Mutation to save the reordering of folders
 */
export const useReorderFolders = () => {
    const userApi : UserApiClient = new UserApiClient(baseUrl);
    const response = useMutation((data: IListFolderDto[]) => {
        const folderList: ListFolderDto[] = [];
        data.forEach(folder => {
            folderList.push(new ListFolderDto(folder));
        });
        return userApi.list_ReorderFolders(folderList);
    });

    return response;
}
//#endregion